import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { DialogContext, type DialogApi } from './dialogContext';
import { Modal } from './Modal';

type Request =
  | { kind: 'confirm'; title: string; message?: ReactNode; confirmLabel: string; cancelLabel: string; danger: boolean; resolve: (v: boolean) => void }
  | { kind: 'alert'; title: string; message?: ReactNode; confirmLabel: string; resolve: () => void }
  | { kind: 'prompt'; title: string; message?: ReactNode; label: string; initial: string; confirmLabel: string; required: boolean; resolve: (v: string | null) => void };

// DialogProvider stellt confirm/alert/prompt als gestaltete Dialoge bereit (useDialog()).
export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Request[]>([]);
  const current = queue[0];
  const push = useCallback((r: Request) => setQueue((q) => [...q, r]), []);
  const pop = useCallback(() => setQueue((q) => q.slice(1)), []);

  const api = useMemo<DialogApi>(
    () => ({
      confirm: (o) =>
        new Promise<boolean>((resolve) =>
          push({ kind: 'confirm', title: o.title, message: o.message, confirmLabel: o.confirmLabel ?? 'OK', cancelLabel: o.cancelLabel ?? 'Abbrechen', danger: !!o.danger, resolve }),
        ),
      alert: (o) => new Promise<void>((resolve) => push({ kind: 'alert', title: o.title, message: o.message, confirmLabel: o.confirmLabel ?? 'OK', resolve })),
      prompt: (o) =>
        new Promise<string | null>((resolve) =>
          push({ kind: 'prompt', title: o.title, message: o.message, label: o.label, initial: o.initial ?? '', confirmLabel: o.confirmLabel ?? 'OK', required: o.required ?? true, resolve }),
        ),
    }),
    [push],
  );

  return (
    <DialogContext value={api}>
      {children}
      {current && <DialogView key={queue.length} request={current} done={pop} />}
    </DialogContext>
  );
}

function DialogView({ request, done }: { request: Request; done: () => void }) {
  const [value, setValue] = useState(request.kind === 'prompt' ? request.initial : '');
  const settled = useRef(false);
  const finish = (fn: () => void) => {
    if (settled.current) return;
    settled.current = true;
    fn();
    done();
  };
  const cancel = () =>
    finish(() => {
      if (request.kind === 'confirm') request.resolve(false);
      else if (request.kind === 'prompt') request.resolve(null);
      else request.resolve();
    });
  const ok = () =>
    finish(() => {
      if (request.kind === 'confirm') request.resolve(true);
      else if (request.kind === 'prompt') request.resolve(value.trim());
      else request.resolve();
    });
  const disabled = request.kind === 'prompt' && request.required && !value.trim();

  return (
    <Modal
      title={request.title}
      onClose={cancel}
      actions={
        <>
          {request.kind !== 'alert' && (
            <button type="button" className="btn" onClick={cancel}>
              {request.kind === 'confirm' ? request.cancelLabel : 'Abbrechen'}
            </button>
          )}
          <button
            type="button"
            className={request.kind === 'confirm' && request.danger ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={disabled}
            onClick={ok}
            data-autofocus={request.kind === 'prompt' ? undefined : true}
          >
            {request.confirmLabel}
          </button>
        </>
      }
    >
      {request.message && <div className="modal-message">{request.message}</div>}
      {request.kind === 'prompt' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!disabled) ok();
          }}
        >
          <label className="field wide">
            <span>{request.label}</span>
            <input value={value} onChange={(e) => setValue(e.target.value)} data-autofocus />
          </label>
        </form>
      )}
    </Modal>
  );
}
